// Focus — surfaces focus_rules + pending Focus-Time Guardian reschedule
// proposals. Two-column layout mirrors Inbox.tsx (PR #24) + Meetings.tsx
// (Phase 1) polish standard.
//
// Read shape:
//   - Left column: focus_rules (read-only V1 — CRUD form is a follow-up).
//   - Right column: approvals(target_type='focus_reschedule', status='pending')
//     joined to the originating calendar_event for context.
//
// Mutation shape:
//   - Approve: UPDATE approvals SET status='approved', decided_by=user.id,
//     decided_at=NOW(). NO calendar.events.patch — owner moves the event
//     manually in Google Calendar (strict N1; mechanically enforced by
//     scripts/check-no-calendar-patch.sh).
//   - Reject: same shape with status='rejected'.
//
// Polish layer (post-prototype-alignment, refined 2026-05-22):
//   - hero PageStatsRow: In focus block | Pending reschedules |
//     Approved this week. The earlier "Active rule now" + "Rules"
//     pairing read as redundant — both used the word "active" for two
//     different concepts (wall-clock-inside-a-window vs is_active toggle).
//     The new lineup replaces the rules-count tile with an outcome
//     metric (reschedules approved over the trailing 7d window).
//   - "Active now" treatment on the rule whose [start, end) window
//     contains the current instant in the rule's IANA timezone. The
//     resolver is the pure helper utils/focusActive.ts — see its tests
//     for window-boundary + cross-tz semantics.
//   - mobile rationale line-clamp + expand toggle on proposal cards.
//
// Screen field audit (CLAUDE.md §19 — every visible field traces to a writer):
//   - "In focus block" status     ← findActiveFocusRule(rulesQ.data, now)
//                                   reads focus_rules table (writer: SQL seed)
//   - active rule label + window  ← focus_rules.label / .start_time / .end_time
//   - "Pending reschedules" count ← approvals where
//                                   target_type='focus_reschedule' AND
//                                   status='pending' (writer: focus_guardian
//                                   daemon)
//   - "Approved this week" count  ← approvals where
//                                   target_type='focus_reschedule' AND
//                                   status='approved' AND
//                                   created_at >= now() - 7d (writer:
//                                   focus_guardian + owner's approve action)

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowRight,
  Calendar,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Inbox,
  Pencil,
  Plus,
  ShieldCheck,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import GlassCard from '@/components/shared/GlassCard';
import PageStatsRow from '@/components/shared/PageStatsRow';
import PulseDot from '@/components/shared/PulseDot';
import FocusRuleModal, { type EditableFocusRule } from '@/components/focus/FocusRuleModal';
import TodayTimeline from '@/components/focus/TodayTimeline';
import GuardianSettingsCard from '@/components/focus/GuardianSettingsCard';
import VIPListCard from '@/components/focus/VIPListCard';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { findActiveFocusRule } from '@/utils/focusActive';

type FocusRuleRow = {
  id: string;
  day_of_week: number; // 0=Sun … 6=Sat
  start_time: string; // 'HH:MM:SS' local in timezone
  end_time: string;
  timezone: string;
  label: string;
  is_active: boolean;
  category_allowlist: string[];
};

type CalendarEventEmbed = {
  id: string;
  title: string | null;
  start_at: string;
  end_at: string | null;
  attendees: Array<{ email?: string; name?: string }>;
};

type FocusApprovalRow = {
  id: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  proposed_event_start: string;
  proposed_event_end: string;
  proposed_calendar_event_id: string;
  proposed_rationale: string;
  trace_id: string;
  calendar_events: CalendarEventEmbed | null;
};

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Hard-coded display timezone for the page. Joel + the seed data both
// live in PT; without this, browser-tz drift (e.g. an Asia/Kolkata
// session) renders Friday-PT events as Saturday-IST and the rationale
// copy ("Conflicts with your Friday 11:00–12:30 block") stops matching
// the visible day in the proposal card. Picking ONE tz for the page
// keeps every time on /focus consistent with every other time, which
// is what the demo needs.
//
// If Phase 1B grows a multi-tz user, this becomes an
// `app_settings.primary_work_tz:<uid>` JSONB read with the same fall-
// back to PT. Not in scope today.
const DISPLAY_TZ = 'America/Los_Angeles';

const FOCUS_APPROVAL_SELECT =
  'id, status, created_at, proposed_event_start, proposed_event_end, proposed_calendar_event_id, proposed_rationale, trace_id, calendar_events:proposed_calendar_event_id (id, title, start_at, end_at, attendees)';

async function fetchFocusRules(): Promise<FocusRuleRow[]> {
  const { data, error } = await supabase
    .from('focus_rules')
    .select('id, day_of_week, start_time, end_time, timezone, label, is_active, category_allowlist')
    .is('deleted_at', null)
    .order('day_of_week', { ascending: true })
    .order('start_time', { ascending: true });
  if (error) throw error;
  return (data as unknown as FocusRuleRow[]) ?? [];
}

async function fetchPendingProposals(): Promise<FocusApprovalRow[]> {
  const { data, error } = await supabase
    .from('approvals')
    .select(FOCUS_APPROVAL_SELECT)
    .eq('target_type', 'focus_reschedule')
    .eq('status', 'pending')
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) throw error;
  return (data as unknown as FocusApprovalRow[]) ?? [];
}

async function fetchRecentlyDecided(): Promise<FocusApprovalRow[]> {
  const { data, error } = await supabase
    .from('approvals')
    .select(FOCUS_APPROVAL_SELECT)
    .eq('target_type', 'focus_reschedule')
    .in('status', ['approved', 'rejected'])
    .order('created_at', { ascending: false })
    .limit(10);
  if (error) throw error;
  return (data as unknown as FocusApprovalRow[]) ?? [];
}

// Approved-this-week count for the hero outcome tile. Uses a count-only
// HEAD request — no rows transferred — so the stat tile costs nothing
// beyond a single round-trip. The window is "trailing 7 days from now",
// matching the Action Items "Due this week" semantic (rolling, not
// calendar-week-locked).
async function fetchApprovedThisWeekCount(): Promise<number> {
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
  const { count, error } = await supabase
    .from('approvals')
    .select('id', { count: 'exact', head: true })
    .eq('target_type', 'focus_reschedule')
    .eq('status', 'approved')
    .gte('created_at', sevenDaysAgo);
  if (error) throw error;
  return count ?? 0;
}

function formatRuleTime(start: string, end: string): string {
  // 'HH:MM:SS' → 'HH:MM' (strip seconds for display)
  const trim = (t: string) => t.slice(0, 5);
  return `${trim(start)}–${trim(end)}`;
}

function formatEventTime(iso: string): string {
  const d = new Date(iso);
  // Pinned to DISPLAY_TZ so the visible day-of-week on a proposal card
  // matches the day the rule says ("conflicts with your Friday block")
  // regardless of the browser's IANA. The trailing " PT" suffix makes
  // the tz contract explicit to the operator at a glance.
  return (
    d.toLocaleString('en-US', {
      timeZone: DISPLAY_TZ,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }) + ' PT'
  );
}

// Pure helper exported for the page tile test. Returns the secondary
// copy for the "In focus block" PageStatsRow tile. Three states:
//   - active rule present → "Yes — <label> · HH:MM–HH:MM"
//   - rules loaded, no active rule → "No block right now"
//   - rules not loaded yet → "Loading…"
export function formatInFocusBlockSecondary(
  activeRule: { label: string; start_time: string; end_time: string } | null,
  hasRulesData: boolean,
): string {
  if (activeRule) {
    return `Yes — ${activeRule.label} · ${formatRuleTime(activeRule.start_time, activeRule.end_time)}`;
  }
  return hasRulesData ? 'No block right now' : 'Loading…';
}

const Focus = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const rulesQ = useQuery({ queryKey: ['focus_rules'], queryFn: fetchFocusRules });
  const pendingQ = useQuery({
    queryKey: ['focus_proposals', 'pending'],
    queryFn: fetchPendingProposals,
  });
  const decidedQ = useQuery({
    queryKey: ['focus_proposals', 'decided'],
    queryFn: fetchRecentlyDecided,
  });
  const approvedWeekQ = useQuery({
    queryKey: ['focus_proposals', 'approved_week_count'],
    queryFn: fetchApprovedThisWeekCount,
  });

  // "Now" used for the active-rule lookup. We refresh it once a minute so
  // the active treatment ticks over at window boundaries without a page
  // reload, but not more often than necessary.
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const activeRule = useMemo(() => findActiveFocusRule(rulesQ.data ?? [], now), [rulesQ.data, now]);

  // Track which proposal card is visually "selected" (highlighted with
  // the forest accent bar). Pure UI state — cards are self-contained
  // (approve/reject on each card), so no detail-pane derived value needed.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  useEffect(() => {
    if (!pendingQ.data) return;
    const ids = pendingQ.data.map((r) => r.id);
    setSelectedId((curr) => (curr && ids.includes(curr) ? curr : (ids[0] ?? null)));
  }, [pendingQ.data]);

  const decide = useMutation({
    mutationFn: async (args: { id: string; status: 'approved' | 'rejected' }) => {
      if (!user) throw new Error('No user');
      const { error } = await supabase
        .from('approvals')
        .update({
          status: args.status,
          decided_by: user.id,
          decided_at: new Date().toISOString(),
          decision_reason:
            args.status === 'approved'
              ? 'approved from focus page — owner will move event manually (N1)'
              : 'rejected from focus page',
        })
        .eq('id', args.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['focus_proposals', 'pending'] });
      qc.invalidateQueries({ queryKey: ['focus_proposals', 'decided'] });
      qc.invalidateQueries({ queryKey: ['focus_proposals', 'approved_week_count'] });
    },
  });

  const rowPending = (id: string): boolean => decide.isPending && decide.variables?.id === id;

  // Modal state for the focus-rule create/edit form. `editingRule`
  // null → "+ Add rule" mode; non-null → edit that rule.
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<EditableFocusRule | undefined>(undefined);

  // Inline is_active toggle on each rule card. Owner UPDATE via RLS.
  const toggleActive = useMutation({
    mutationFn: async (args: { id: string; nextActive: boolean }) => {
      const { error } = await supabase
        .from('focus_rules')
        .update({ is_active: args.nextActive })
        .eq('id', args.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['focus_rules'] });
    },
    onError: (err) => {
      // The toggle drives the Guardian agent's behavior — a silent write
      // failure would leave the UI showing a state the server never accepted.
      toast.error(
        err instanceof Error ? err.message : 'Could not update the rule. Please try again.',
      );
      qc.invalidateQueries({ queryKey: ['focus_rules'] });
    },
  });

  function openCreate(): void {
    setEditingRule(undefined);
    setModalOpen(true);
  }
  function openEdit(rule: FocusRuleRow): void {
    setEditingRule({
      id: rule.id,
      day_of_week: rule.day_of_week,
      start_time: rule.start_time,
      end_time: rule.end_time,
      timezone: rule.timezone,
      label: rule.label,
      is_active: rule.is_active,
      category_allowlist: rule.category_allowlist,
    });
    setModalOpen(true);
  }

  const pendingCount = pendingQ.data?.length ?? 0;
  const activeRuleCount = (rulesQ.data ?? []).filter((r) => r.is_active).length;
  const approvedWeekCount = approvedWeekQ.data ?? 0;

  // Stats panel values: undefined while the underlying query is loading
  // (em-dash), numbers once loaded.
  //
  // The "In focus block" tile is text-only — `value` stays null and the
  // status reads from `secondary`. PageStatsRow renders the em-dash for
  // null, which is what we want when no rule is currently active.
  // Status copy:
  //   - active: "Yes — <label> · HH:MM–HH:MM"
  //   - none:   "No block right now"
  //   - loading: "Loading…"
  const hasRulesData = !!rulesQ.data;
  const hasPendingData = !!pendingQ.data;
  const hasApprovedWeekData = approvedWeekQ.data !== undefined;

  return (
    <div className="space-y-6">
      <motion.header
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="flex items-start justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-serif font-semibold tracking-tight text-foreground">
            Focus Time
          </h1>
          <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
            {activeRuleCount === 0
              ? 'No focus rules configured. The Guardian agent has nothing to defend.'
              : `${activeRuleCount} focus rule${activeRuleCount === 1 ? '' : 's'} configured. ${pendingCount} reschedule proposal${pendingCount === 1 ? '' : 's'} awaiting approval — the agent suggests; you move events manually in Google Calendar (N1).`}
          </p>
        </div>
      </motion.header>

      <PageStatsRow
        stats={[
          {
            label: 'In focus block',
            // Value is intentionally null — the status text + rule label
            // is the payload, surfaced via `secondary`. PageStatsRow
            // renders an em-dash where the count would normally sit,
            // which is the right visual when "no block right now".
            value: null,
            icon: Clock,
            iconClassName: activeRule ? 'text-forest' : 'text-muted-foreground',
            valueClassName: activeRule ? 'text-forest' : 'text-muted-foreground',
            secondary: formatInFocusBlockSecondary(activeRule, hasRulesData),
          },
          {
            label: 'Pending reschedules',
            value: hasPendingData ? pendingCount : undefined,
            icon: Inbox,
            iconClassName: pendingCount > 0 ? 'text-attention' : 'text-muted-foreground',
            valueClassName: pendingCount > 0 ? 'text-attention' : 'text-foreground',
            secondary: hasPendingData
              ? pendingCount === 0
                ? 'queue clear'
                : 'awaiting approval'
              : undefined,
          },
          {
            label: 'Approved this week',
            value: hasApprovedWeekData ? approvedWeekCount : undefined,
            icon: CheckCircle2,
            iconClassName: approvedWeekCount > 0 ? 'text-sage' : 'text-muted-foreground',
            valueClassName: approvedWeekCount > 0 ? 'text-sage' : 'text-foreground',
            secondary: hasApprovedWeekData ? 'trailing 7 days' : undefined,
          },
        ]}
      />

      <TodayTimeline rules={rulesQ.data ?? []} displayTz={DISPLAY_TZ} />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Left column — Focus Rules */}
        <div className="lg:col-span-2 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-[11px] uppercase tracking-wide text-muted-foreground/80">
              Focus rules
            </h2>
            <Button type="button" size="sm" variant="outline" onClick={openCreate}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add rule
            </Button>
          </div>
          {rulesQ.isLoading && <p className="text-sm text-muted-foreground">Loading rules…</p>}
          {rulesQ.isError && (
            <GlassCard hover={false} className="neo-alert">
              <p className="text-sm text-alert">
                Failed to load rules: {(rulesQ.error as Error).message}
              </p>
            </GlassCard>
          )}
          {rulesQ.data && rulesQ.data.length === 0 && (
            <GlassCard hover={false} variant="inset">
              <p className="text-sm text-muted-foreground">
                No focus rules yet — add one to give the Guardian agent something to defend.
              </p>
            </GlassCard>
          )}
          <div className="space-y-3">
            {(rulesQ.data ?? []).map((rule) => (
              <FocusRuleCard
                key={rule.id}
                rule={rule}
                isActiveNow={activeRule?.id === rule.id}
                togglePending={toggleActive.isPending && toggleActive.variables?.id === rule.id}
                onEdit={() => openEdit(rule)}
                onToggleActive={() =>
                  toggleActive.mutate({ id: rule.id, nextActive: !rule.is_active })
                }
              />
            ))}
          </div>
        </div>

        {/* Right column — Pending Proposals */}
        <div className="lg:col-span-3 space-y-3">
          <h2 className="text-[11px] uppercase tracking-wide text-muted-foreground/80">
            Pending reschedule proposals · {pendingCount}
          </h2>
          {pendingQ.isLoading && (
            <p className="text-sm text-muted-foreground">Loading proposals…</p>
          )}
          {pendingQ.isError && (
            <GlassCard hover={false} className="neo-alert">
              <p className="text-sm text-alert">
                Failed to load: {(pendingQ.error as Error).message}
              </p>
            </GlassCard>
          )}
          {pendingQ.data && pendingQ.data.length === 0 && !pendingQ.isLoading && (
            <GlassCard hover={false} variant="inset">
              <p className="text-sm text-muted-foreground">
                No pending proposals. The Guardian agent scans upcoming events every minute.
              </p>
              <p className="mt-2 text-[11px] text-muted-foreground/80">
                <ShieldCheck className="w-3 h-3 inline mr-1" />
                Reschedule proposals require your approval. We never move events for you.
              </p>
            </GlassCard>
          )}
          <AnimatePresence mode="popLayout" initial={false}>
            {(pendingQ.data ?? []).map((proposal) => (
              <motion.div
                key={proposal.id}
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                transition={{ duration: 0.25 }}
              >
                <ProposalCard
                  proposal={proposal}
                  selected={proposal.id === selectedId}
                  pending={rowPending(proposal.id)}
                  onSelect={() => setSelectedId(proposal.id)}
                  onApprove={() => decide.mutate({ id: proposal.id, status: 'approved' })}
                  onReject={() => decide.mutate({ id: proposal.id, status: 'rejected' })}
                />
              </motion.div>
            ))}
          </AnimatePresence>
          <p className="text-[11px] text-muted-foreground/80 mt-3">
            <ShieldCheck className="w-3 h-3 inline mr-1" />
            Approving marks the proposal — Joel/Sadiq moves the event manually in Google Calendar.
            The agent never calls Calendar's mutation APIs (N1, mechanically gated).
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <GuardianSettingsCard />
        <VIPListCard />
      </div>

      <section className="space-y-3">
        <h2 className="text-lg font-medium tracking-tight">Recently decided</h2>
        {decidedQ.data && decidedQ.data.length === 0 && (
          <p className="text-sm text-muted-foreground">Nothing decided yet.</p>
        )}
        <ul className="space-y-2">
          {(decidedQ.data ?? []).map((row) => (
            <DecidedItem key={row.id} row={row} />
          ))}
        </ul>
      </section>

      <FocusRuleModal open={modalOpen} onOpenChange={setModalOpen} rule={editingRule} />
    </div>
  );
};

type FocusRuleCardProps = {
  rule: FocusRuleRow;
  isActiveNow: boolean;
  togglePending: boolean;
  onEdit: () => void;
  onToggleActive: () => void;
};

function FocusRuleCard({
  rule,
  isActiveNow,
  togglePending,
  onEdit,
  onToggleActive,
}: FocusRuleCardProps) {
  // "Active now" treatment: a ring + tinted background on the GlassCard,
  // plus a PulseDot + "Active now" pill in the label row. The two cues
  // (color + animated dot) ensure the state is visible to users with
  // reduced color discrimination.
  //
  // Click anywhere on the card → open the edit modal. The toggle switch
  // and edit button stop propagation so they don't double-fire.
  return (
    <GlassCard
      hover={false}
      onClick={onEdit}
      ariaPressed={false}
      className={`cursor-pointer transition-all ${
        isActiveNow ? 'neo-pulse-glow' : rule.is_active ? '' : 'opacity-60'
      } ${togglePending ? 'opacity-50' : ''}`}
    >
      <div className="flex items-start gap-3">
        <div className="mt-1 shrink-0">
          {rule.is_active ? (
            <PulseDot color="green" />
          ) : (
            <span className="block w-2 h-2 rounded-full bg-muted-foreground/40" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-foreground truncate">{rule.label}</p>
            {isActiveNow && (
              <span className="flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full neo-chip text-forest">
                <PulseDot color="green" /> Active now
              </span>
            )}
            {!rule.is_active && (
              <Badge variant="secondary" className="text-[10px]">
                inactive
              </Badge>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
            <Calendar className="w-3 h-3" />
            {DAY_NAMES[rule.day_of_week]}
            <span aria-hidden="true">·</span>
            <Clock className="w-3 h-3" />
            {formatRuleTime(rule.start_time, rule.end_time)}
            <span className="font-mono opacity-70">({rule.timezone})</span>
          </p>
          {(rule.category_allowlist ?? []).length > 0 && (
            <p className="text-[10px] text-muted-foreground mt-1">
              Allowed inside block: {(rule.category_allowlist ?? []).join(', ')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {/* Inline is_active toggle. role=switch for a11y. Stops propagation
              so the parent's "open edit modal" click handler doesn't fire. */}
          <button
            type="button"
            role="switch"
            aria-checked={rule.is_active}
            aria-label={rule.is_active ? 'Pause rule' : 'Activate rule'}
            disabled={togglePending}
            onClick={(e) => {
              e.stopPropagation();
              onToggleActive();
            }}
            className={`w-9 h-5 rounded-full relative cursor-pointer transition-colors duration-200 ${
              rule.is_active ? 'bg-forest/40' : 'bg-secondary'
            } ${togglePending ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`block w-4 h-4 rounded-full shadow-sm absolute top-0.5 transition-all duration-200 ${
                rule.is_active ? 'right-0.5 bg-forest' : 'left-0.5 bg-muted-foreground/40'
              }`}
            />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            aria-label="Edit rule"
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </GlassCard>
  );
}

type ProposalCardProps = {
  proposal: FocusApprovalRow;
  selected: boolean;
  pending: boolean;
  onSelect: () => void;
  onApprove: () => void;
  onReject: () => void;
};

function ProposalCard({
  proposal,
  selected,
  pending,
  onSelect,
  onApprove,
  onReject,
}: ProposalCardProps) {
  const event = proposal.calendar_events;
  const currentTime = event ? formatEventTime(event.start_at) : '(event missing)';
  const proposedTime = formatEventTime(proposal.proposed_event_start);
  // Mobile rationale collapse — see scope item 3. We use sm: as the cutoff
  // (640px) so the toggle disappears on tablet+, matching the default
  // grid behaviour where the right column has more horizontal room.
  const [expanded, setExpanded] = useState(false);
  return (
    <GlassCard
      hover={false}
      ariaPressed={selected}
      className={`cursor-pointer transition-all relative ${
        selected
          ? 'neo-selected before:absolute before:left-1 before:top-3 before:bottom-3 before:w-1 before:rounded-r before:bg-forest'
          : ''
      } ${pending ? 'opacity-70' : ''}`}
      onClick={onSelect}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground truncate">
            {event?.title ?? '(no title)'}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5 font-mono">
            {currentTime} <ArrowRight className="inline w-3 h-3 mx-1" /> {proposedTime}
          </p>
        </div>
        <span className="text-[11px] font-mono text-muted-foreground/70 shrink-0">
          {formatDistanceToNow(new Date(proposal.created_at), { addSuffix: true })}
        </span>
      </div>
      {/* Rationale: line-clamp-3 below sm, full text at sm+. The Expand
          toggle is rendered ONLY at <sm. At sm+ the clamp class is reset
          (line-clamp-none) so all text shows and the toggle row is
          hidden via sm:hidden. */}
      <p
        className={`text-sm text-foreground leading-relaxed mt-2 mb-2 sm:line-clamp-none ${
          expanded ? 'line-clamp-none' : 'line-clamp-3'
        }`}
      >
        {proposal.proposed_rationale}
      </p>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setExpanded((v) => !v);
        }}
        aria-expanded={expanded}
        className="mb-3 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors sm:hidden"
      >
        {expanded ? (
          <>
            <ChevronDown className="w-3 h-3" aria-hidden="true" />
            Collapse
          </>
        ) : (
          <>
            <ChevronRight className="w-3 h-3" aria-hidden="true" />
            Expand
          </>
        )}
      </button>
      <div className="flex items-center justify-end gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={(e) => {
            e.stopPropagation();
            onReject();
          }}
          disabled={pending}
          aria-label="Reject proposal"
        >
          <X className="w-4 h-4 mr-1" />
          {pending ? '…' : 'Reject'}
        </Button>
        <Button
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onApprove();
          }}
          disabled={pending}
          aria-label="Approve proposal (move event manually in Google Calendar)"
        >
          <Check className="w-4 h-4 mr-1" />
          {pending ? 'Approving…' : 'Approve — I will move it'}
        </Button>
      </div>
    </GlassCard>
  );
}

function DecidedItem({ row }: { row: FocusApprovalRow }) {
  const variant: 'default' | 'secondary' | 'destructive' =
    row.status === 'rejected' ? 'destructive' : 'default';
  const label = row.status;
  return (
    <li className="flex items-center justify-between gap-3 neo-inset px-4 py-2 text-sm">
      <div className="min-w-0 flex-1 truncate">
        <span className="mr-2 text-muted-foreground font-mono text-xs">
          {formatDistanceToNow(new Date(row.created_at), { addSuffix: true })}
        </span>
        {row.calendar_events?.title ?? '(event)'} → {formatEventTime(row.proposed_event_start)}
      </div>
      <Badge variant={variant}>{label}</Badge>
    </li>
  );
}

export default Focus;
